import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useEffect, useRef, useState, useMemo } from 'react';
import {
    Platform,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n/useT';
import { getThemeColors } from '../utils/themeStyles';
import { getScenarioForMission } from '../data/scenarios';
import { TYPE_COLOR, glow, glowText, type Palette } from '../utils/cyb';
import { usePalette } from '../hooks/usePalette';
import ActionBar from '../components/ActionBar';
import { useAuth } from '../context/AuthContext';
import { stopItemTimer } from '../api/missionTimers';
import { envoyerReponsesQuiz } from '../api/storyAnswers';
import ElementHeader from '../components/ElementHeader';
import HeaderNav from '../components/HeaderNav';
import { useItemNav } from '../utils/itemNav';
import { useElementVisited } from '../utils/elementProgress';

// Header de navigation TOUJOURS visible au scroll (CCT-193 #7). Le header est déjà hors du
// ScrollView, mais sur mobile web le DOCUMENT peut scroller (URL bar) → on épingle explicitement.
const WEB_STICKY: any = Platform.OS === 'web' ? { position: 'sticky', top: 0, zIndex: 10 } : null;

interface Question {
  id: number;
  question: string;
  type: 'single_choice' | 'multi_choice';
  options: string[];
  correctAnswers: number[];
  explanation: string;
}

interface Quiz {
  id: number;
  title: string;
  description: string;
  duration: number;
  totalQuestions: number;
  passingScore: number;
  status: 'not_started' | 'in_progress' | 'completed' | 'failed';
  userScore?: number;
  attempts: number;
  maxAttempts: number;
  questions: Question[];
  lastAttempt?: string;
}

// ⚠️ PLUS DE QUIZ BUNDLÉS ICI (CCT-285, même décision que CCT-96 et CCT-253).
//
// Il en restait deux, complets — questions, options, explications — servis EN REPLI
// SILENCIEUX quand le scénario n'en rendait aucun : `fromScenario.length ? … : BUNDLED_QUIZZES`.
// Le joueur passait alors un quiz d'une AUTRE mission, crédible et sans rapport, sans que
// rien ne le signale — et son score était calculé dessus.
//
// C'est le même repli que celui retiré des scènes, des chats et des fiches. Celui-ci avait
// survécu au nettoyage. Un écran vide QUI SE VOIT vaut mieux qu'un contenu qui ne mène
// nulle part.

export default function QuizScreen({ navigation: navProp, route: routeProp }: { navigation?: any; route?: any } = {}) {
  const C = usePalette();
  const styles = useMemo(() => makeStyles(C), [C]);
  const navigation = navProp ?? useNavigation<any>();
  const route = routeProp ?? useRoute<any>();
  const itemNav = useItemNav(route, navigation);
  // Progression PILOTEE PAR LE MODELE (cf. utils/elementProgress) : c'est l'element
  // AFFICHE qui est enregistre, quel que soit le chemin emprunte pour l'ouvrir.
  const { getAccessToken } = useAuth();
  useElementVisited(route?.params?.missionId, route?.params?.itemId);

  const scrollRef = useRef<ScrollView>(null);
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const t = useT();

  // Accent de CET écran : la couleur du type d'élément (quiz = violet). C'est elle qui
  // pilote toutes les surbrillances — pas une couleur générique de thème.
  const ACCENT = TYPE_COLOR.quiz;

  const [selectedQuiz, setSelectedQuiz] = useState<number | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [userAnswers, setUserAnswers] = useState<{ [questionId: number]: number[] }>({});
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(15 * 60);
  // CCT-121 — copie du JOUEUR conservée par quiz : « Revoir » repartait d'un état vide, donc
  // n'affichait que les bonnes réponses (et tombait sur un écran « Revue complète » parasite
  // quand l'index de question était hors bornes).
  const [answersByQuiz, setAnswersByQuiz] = useState<{ [quizId: number]: { answers: { [qid: number]: number[] }; score: number } }>({});

  const [quizzes, setQuizzes] = useState<Quiz[]>([]);

  // Les quiz JOUÉS sont ceux de la mission (scénario servi depuis la base) : l'écran avait une
  // liste codée en dur, d'où les quiz d'Ego Crisis sur toute mission. Sans quiz authoré, la
  // liste reste VIDE et l'écran le dit — il n'y a plus de repli bundlé (cf. en-tête).
  const missionId: number | undefined = route.params?.missionId;
  const chapterId: number | undefined = route.params?.chapterId;
  useEffect(() => {
    const scenario = missionId != null ? getScenarioForMission(missionId) : undefined;
    const chapter = scenario?.chapters?.[(chapterId ?? 1) - 1] ?? scenario?.chapters?.[0];
    const items = (chapter?.sequence ?? []).filter((it: any) => it.type === 'quiz');
    const fromScenario: Quiz[] = items.map((it: any, i: number) => {
      const c = it.content ?? {};
      const questions: Question[] = (c.questions ?? []).map((q: any, qi: number) => ({
        id: qi + 1, question: q.question, type: q.type === 'multi_choice' ? 'multi_choice' : 'single_choice',
        options: q.options ?? [], correctAnswers: q.correctAnswers ?? [], explanation: q.explanation ?? '',
      }));
      return {
        id: i + 1, title: it.title || 'Quiz', description: c.description ?? '',
        duration: Number(c.duration) || 10, totalQuestions: questions.length,
        passingScore: Number(c.passingScore) || 70, status: 'not_started',
        attempts: 0, maxAttempts: Number(c.maxAttempts) || 3, questions,
      };
    });
    setQuizzes(fromScenario);
  }, [missionId, chapterId]);


  const currentQuiz = selectedQuiz ? quizzes.find(q => q.id === selectedQuiz) : null;
  const currentQuestion = currentQuiz?.questions[currentQuestionIndex];

  // Remise du défilement en haut à chaque changement de vue ou de question : les vues
  // partagent le même ScrollView, on héritait donc de la position de la précédente.
  useEffect(() => {
    scrollRef.current?.scrollTo({ y: 0, animated: false });
  }, [selectedQuiz, currentQuestionIndex, quizCompleted]);

  useEffect(() => {
    if (selectedQuiz && !quizCompleted && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) { handleCalculateResults(); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [selectedQuiz, quizCompleted, timeLeft]);

  const handleAnswerSelect = (optionIndex: number) => {
    if (!currentQuestion) return;
    if (currentQuestion.type === 'single_choice') {
      setSelectedAnswers([optionIndex]);
    } else {
      setSelectedAnswers(prev =>
        prev.includes(optionIndex) ? prev.filter(i => i !== optionIndex) : [...prev, optionIndex]
      );
    }
  };

  const handleNextQuestion = () => {
    if (!currentQuestion || !currentQuiz) return;
    const updatedAnswers = { ...userAnswers, [currentQuestion.id]: selectedAnswers };
    setUserAnswers(updatedAnswers);
    if (currentQuestionIndex < currentQuiz.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswers([]);
    } else {
      handleCalculateResults(updatedAnswers);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0 && currentQuestion) {
      setUserAnswers(prev => ({ ...prev, [currentQuestion.id]: selectedAnswers }));
      const prevQuestion = currentQuiz?.questions[currentQuestionIndex - 1];
      setCurrentQuestionIndex(prev => prev - 1);
      if (prevQuestion) setSelectedAnswers(userAnswers[prevQuestion.id] || []);
    }
  };

  const handleCalculateResults = (answers?: { [questionId: number]: number[] }) => {
    if (!currentQuiz) return;
    const finalAnswers = answers ?? userAnswers;
    let correct = 0;
    currentQuiz.questions.forEach(question => {
      const given = finalAnswers[question.id] || [];
      const ok = given.length === question.correctAnswers.length && question.correctAnswers.every(a => given.includes(a));
      if (ok) correct++;
    });
    const score = Math.round((correct / currentQuiz.questions.length) * 100);
    const passed = score >= currentQuiz.passingScore;
    setFinalScore(score);
    setAnswersByQuiz(prev => ({ ...prev, [currentQuiz.id]: { answers: finalAnswers, score } }));
    setQuizzes(prev => prev.map(q =>
      q.id === currentQuiz.id
        ? { ...q, status: passed ? 'completed' : 'failed', userScore: score, attempts: q.attempts + 1, lastAttempt: new Date().toISOString().split('T')[0] }
        : q
    ));
    setQuizCompleted(true);
    // ⚠️ RENDRE ARRÊTE L'HORLOGE — ET SEULEMENT RENDRE (CCT-288). L'ouverture du quiz est déjà
    // enregistrée par `useElementVisited` ; si l'arrêt vivait là, tout chronomètre mourrait à
    // son armement. C'est ce geste-ci, la validation des réponses, qui clôt le temps.
    //
    // Le serveur reste juge : appelé après l'échéance, il laisse le chronomètre expiré et la
    // sanction choisie par l'auteur s'applique. Un échec réseau ne change donc rien au verdict,
    // ce qui est exactement pourquoi on n'attend pas cette réponse pour afficher le score.
    const token = getAccessToken?.();
    const idElement = route?.params?.itemId;
    if (token && route?.params?.missionId && idElement != null) {
      void stopItemTimer(token, Number(route.params.missionId), String(idElement));
      // ⚠️ CE QUE LE JOUEUR A RÉPONDU SORT ENFIN DU NAVIGATEUR — CCT-427. Jusqu'ici le score
      // vivait le temps d'un écran : calculé ici, affiché, puis perdu. Mesuré sur un parcours
      // complet à deux joueurs — trois quiz ouverts, trois quiz marqués faits, et AUCUNE trace
      // en base : ni réponse, ni score, ni tentative. Deux apprenants aux antipodes laissaient
      // le même journal.
      //
      // On envoie les RÉPONSES ; le serveur note contre l'authoring. Le score ci-dessus part
      // avec elles pour être RANGÉ à côté du sien, pas pour faire foi — nous détenons les bonnes
      // réponses, donc notre verdict ne peut pas être la référence.
      //
      // `void` et pas `await` : l'écran de résultat s'affiche sans attendre. Un échec réseau
      // coûte une ligne de journal, jamais le retour du joueur.
      void envoyerReponsesQuiz(
        token, Number(route.params.missionId), String(idElement), finalAnswers, score);
    }
  };

  const handleRetakeQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setUserAnswers({});
    setQuizCompleted(false);
    setFinalScore(0);
    setTimeLeft((currentQuiz?.duration || 15) * 60);
  };

  const handleStartQuiz = (quizId: number) => {
    setSelectedQuiz(quizId);
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setUserAnswers({});
    setQuizCompleted(false);
    setFinalScore(0);
    const quiz = quizzes.find(q => q.id === quizId);
    setTimeLeft((quiz?.duration || 15) * 60);
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return colors.success;
      case 'failed': return colors.error;
      case 'in_progress': return ACCENT;
      default: return colors.textTertiary;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return t('quiz.status.completed');
      case 'failed': return t('quiz.status.failed');
      case 'in_progress': return t('quiz.status.inProgress');
      default: return t('quiz.notStarted');
    }
  };

  // Results screen with per-question explanations
  if (quizCompleted && currentQuiz) {
    const score = finalScore;
    const passed = score >= currentQuiz.passingScore;

    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={[styles.header, WEB_STICKY, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          <ElementHeader
            missionTitle={(route.params as any)?.missionTitle ?? ''}
            chapterTitle={(route.params as any)?.chapterTitle ?? ''}
            elementTitle={t('quiz.results.title')}
            onBack={() => { setSelectedQuiz(null); setQuizCompleted(false); }}
            nav={(
              /* CCT-164 — l'écran de RÉSULTATS n'offrait aucun moyen de poursuivre l'histoire :
                 il fallait revenir à la timeline. Les flèches y sont, comme ailleurs. */
              <HeaderNav onPrev={itemNav.goPrev} onNext={itemNav.goNext} accent={ACCENT} isLast={itemNav.isLast} />
            )}
          />
        </View>

        <ScrollView ref={scrollRef} style={styles.content} showsVerticalScrollIndicator={false}>
          <View style={styles.contentPadding}>
            <View style={[styles.scoreCard, { backgroundColor: colors.cardBackground }]}>
              <View style={[styles.resultIcon, { backgroundColor: passed ? colors.success : colors.error }]}>
                <Ionicons name={passed ? 'trophy' : 'close-circle'} size={40} color="#FFF" />
              </View>
              <Text style={[styles.resultTitle, { color: colors.textPrimary }]}>
                {passed ? 'Bravo !' : t('quiz.results.failed')}
              </Text>
              <Text style={[styles.resultSubtitle, { color: colors.textSecondary }]}>
                {passed ? t('quiz.results.passed') : t('quiz.results.retry')}
              </Text>
              <View style={styles.scoreRow}>
                <View style={styles.scoreItem}>
                  <Text style={[styles.scoreNumber, { color: passed ? colors.success : colors.error }]}>{score}%</Text>
                  <Text style={[styles.scoreLabel, { color: colors.textSecondary }]}>{t('quiz.results.score')}</Text>
                </View>
                <View style={[styles.scoreDivider, { backgroundColor: colors.border }]} />
                <View style={styles.scoreItem}>
                  <Text style={[styles.scoreNumber, { color: colors.textPrimary }]}>{currentQuiz.passingScore}%</Text>
                  <Text style={[styles.scoreLabel, { color: colors.textSecondary }]}>{t('quiz.requiredScore')}</Text>
                </View>
                <View style={[styles.scoreDivider, { backgroundColor: colors.border }]} />
                <View style={styles.scoreItem}>
                  <Text style={[styles.scoreNumber, { color: colors.textPrimary }]}>
                    {currentQuiz.questions.filter(q => {
                      const given = userAnswers[q.id] || [];
                      return given.length === q.correctAnswers.length && q.correctAnswers.every(a => given.includes(a));
                    }).length}/{currentQuiz.questions.length}
                  </Text>
                  <Text style={[styles.scoreLabel, { color: colors.textSecondary }]}>{t('quiz.correctCount')}</Text>
                </View>
              </View>
            </View>

            <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>{t('quiz.review.title')}</Text>
            {/* Les réponses ne sont pas persistées côté serveur : après un rechargement de l'app,
                on ne peut afficher que le corrigé. Le dire plutôt que laisser croire que le
                joueur n'avait rien coché (toutes les questions apparaîtraient « ratées »). */}
            {Object.keys(userAnswers).length === 0 && (
              <Text style={{ fontSize: 12, color: colors.textTertiary, marginBottom: 12 }}>
                {t('quiz.previousAnswersGone')}
              </Text>
            )}
            {currentQuiz.questions.map((question, qIndex) => {
              const given = userAnswers[question.id] || [];
              const isCorrect = given.length === question.correctAnswers.length && question.correctAnswers.every(a => given.includes(a));
              return (
                <View key={question.id} style={[styles.reviewCard, { backgroundColor: colors.cardBackground, borderLeftColor: isCorrect ? colors.success : colors.error }]}>
                  <View style={styles.reviewHeader}>
                    <View style={[styles.reviewBadge, { backgroundColor: isCorrect ? colors.success : colors.error }]}>
                      <Ionicons name={isCorrect ? 'checkmark' : 'close'} size={12} color="#FFF" />
                    </View>
                    <Text style={[styles.reviewQuestionNum, { color: colors.textSecondary }]}>Question {qIndex + 1}</Text>
                  </View>
                  <Text style={[styles.reviewQuestionText, { color: colors.textPrimary }]}>{question.question}</Text>
                  <View style={styles.reviewOptions}>
                    {question.options.map((option, optIndex) => {
                      const isChosen = given.includes(optIndex);
                      const isCorrectAns = question.correctAnswers.includes(optIndex);
                      let bgColor = colors.surfaceSecondary;
                      let borderColor = colors.border;
                      let textColor = colors.textPrimary;
                      if (isChosen && isCorrectAns) { bgColor = isDarkTheme ? '#064E3B' : '#D1FAE5'; borderColor = '#10B981'; textColor = isDarkTheme ? '#6EE7B7' : '#065F46'; }
                      else if (isChosen && !isCorrectAns) { bgColor = isDarkTheme ? '#7F1D1D' : '#FEE2E2'; borderColor = '#EF4444'; textColor = isDarkTheme ? '#FCA5A5' : '#991B1B'; }
                      else if (!isChosen && isCorrectAns) { bgColor = isDarkTheme ? '#032912' : '#F0FDF4'; borderColor = '#10B981'; textColor = isDarkTheme ? '#6EE7B7' : '#065F46'; }
                      return (
                        <View key={optIndex} style={[styles.reviewOption, { backgroundColor: bgColor, borderColor }]}>
                          <Text style={[styles.reviewOptionText, { color: textColor }]}>{option}</Text>
                          {isChosen && !isCorrectAns && <Ionicons name="close-circle" size={16} color="#EF4444" />}
                          {isCorrectAns && <Ionicons name="checkmark-circle" size={16} color="#10B981" />}
                        </View>
                      );
                    })}
                  </View>
                  <View style={[styles.explanationBox, { backgroundColor: isDarkTheme ? '#1E293B' : '#F8FAFC', borderColor: colors.border, borderLeftColor: ACCENT }]}>
                    <Text style={[styles.explanationLbl, { color: ACCENT }]}>{t('quiz.explanation')}</Text>
                    <Text style={[styles.explanationText, { color: colors.textSecondary }]}>{question.explanation}</Text>
                  </View>
                </View>
              );
            })}

          </View>
        </ScrollView>

        {/* ANCRÉE : les actions de fin de quiz étaient au bas d'un récapitulatif long. */}
        <ActionBar>
          {!passed && (currentQuiz.attempts ?? 0) < currentQuiz.maxAttempts && (
            <TouchableOpacity style={[styles.secondaryButton, { borderColor: C.border }]} onPress={handleRetakeQuiz}>
              <Ionicons name="refresh" size={16} color={ACCENT} />
              <Text style={[styles.secondaryButtonText, { color: ACCENT }]}>{t('quiz.restart')}</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity style={[styles.primaryButton, glow(ACCENT, true, isDarkTheme)]} onPress={() => { setSelectedQuiz(null); setQuizCompleted(false); }}>
            <Text style={[styles.primaryButtonText, { color: glowText(ACCENT, true, isDarkTheme) }]}>{t('quiz.backToQuizzes')}</Text>
          </TouchableOpacity>
        </ActionBar>
      </SafeAreaView>
    );
  }

  // CCT-121 — le mode « revue » séparé (paginé, sans les réponses du joueur) est SUPPRIMÉ :
  // il redéveloppait une 2e vue de correction, montrait uniquement les bonnes réponses et
  // affichait un écran « Revue complète » parasite dès que l'index de question sortait des
  // bornes. « Revoir » réutilise désormais le récapitulatif de fin de quiz, qui confronte déjà
  // la copie du joueur et le corrigé.

  // Active quiz
  if (selectedQuiz && currentQuestion && !quizCompleted) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={[styles.header, WEB_STICKY, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          <View style={styles.headerContent}>
            <TouchableOpacity style={styles.backButton} onPress={() => setSelectedQuiz(null)}>
              <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
            </TouchableOpacity>
            <View style={styles.headerInfo}>
              <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>{currentQuiz?.title}</Text>
              <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>Question {currentQuestionIndex + 1} / {currentQuiz?.questions.length}</Text>
            </View>
            <View style={styles.timerContainer}>
              <Ionicons name="time" size={16} color={C.textMute} />
              <Text style={[styles.timerText, { color: C.textMute }]}>{formatTime(timeLeft)}</Text>
            </View>
            {/* Navigation entre questions : la flèche « suivant » reste inactive tant qu'aucune
                réponse n'est choisie — la règle est ainsi visible sans texte. */}
            <HeaderNav
              onPrev={currentQuestionIndex > 0 ? handlePreviousQuestion : itemNav.goPrev}
              onNext={selectedAnswers.length > 0 ? handleNextQuestion : undefined}
              accent={ACCENT}
              isLast={currentQuestionIndex === (currentQuiz?.questions.length ?? 1) - 1}
            />
          </View>
          <View style={[styles.progressBar, { backgroundColor: colors.surfaceSecondary }]}>
            <View style={[styles.progressFill, { backgroundColor: ACCENT, width: `${((currentQuestionIndex + 1) / (currentQuiz?.questions.length ?? 1)) * 100}%` }]} />
          </View>
        </View>
        <ScrollView ref={scrollRef} style={styles.content} showsVerticalScrollIndicator={false}>
          <View style={styles.contentPadding}>
            <View style={[styles.questionCard, { backgroundColor: colors.cardBackground }]}>
              <Text style={[styles.questionText, { color: colors.textPrimary }]}>{currentQuestion.question}</Text>
              {/* Indique le MODE de réponse (CCT-193 #6) : choix unique (radio) vs multiple (case). */}
              <Text style={[styles.choiceHint, { color: colors.textSecondary }]}>
                {currentQuestion.type === 'multi_choice'
                  ? t('quiz.multiChoice')
                  : t('quiz.singleChoice')}
              </Text>
              <View style={styles.optionsContainer}>
                {currentQuestion.options.map((option, index) => {
                  const isSelected = selectedAnswers.includes(index);
                  const multi = currentQuestion.type === 'multi_choice';
                  // Radio (choix unique) / case à cocher (choix multiple), reflétant aussi la sélection.
                  const icon = multi
                    ? (isSelected ? 'checkbox' : 'square-outline')
                    : (isSelected ? 'radio-button-on' : 'radio-button-off');
                  return (
                    <TouchableOpacity key={index} style={[styles.optionItem, glow(ACCENT, isSelected, isDarkTheme)]} onPress={() => handleAnswerSelect(index)}>
                      <Ionicons name={icon} size={20} color={isSelected ? ACCENT : colors.textSecondary} style={{ marginRight: 10 }} />
                      <Text style={[styles.optionText, { color: glowText(ACCENT, isSelected, isDarkTheme) }]}>{option}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    );
  }

  // Quiz list
  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={[styles.header, WEB_STICKY, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={styles.headerContent}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
          </TouchableOpacity>
          <View style={styles.headerInfo}>
            <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>{t('quiz.title')}</Text>
            <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
              {route.params?.chapterId ? t('quiz.chapter', { n: route.params.chapterId }) : t('quiz.subtitle')}
            </Text>
          </View>
          {/* Vue d'entrée de l'élément : les flèches franchissent l'élément (les vues
              « question » et « revue » ont les leurs, qui parcourent les questions). */}
          <HeaderNav onPrev={itemNav.goPrev} onNext={itemNav.goNext} accent={ACCENT} isLast={itemNav.isLast} />
        </View>
      </View>
      <ScrollView ref={scrollRef} style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.contentPadding}>
          {/* ⚠️ L'ÉTAT VIDE EST LA CONTREPARTIE DU RETRAIT DU REPLI. Supprimer les quiz
              bundlés sans rien mettre à la place remplacerait un faux contenu par un écran
              muet — le joueur ne saurait toujours pas ce qui se passe. Ici, il le lit. */}
          {quizzes.length === 0 && (
            <View style={[styles.quizCard, { backgroundColor: colors.cardBackground,
                                             alignItems: 'center', gap: 8 }]}>
              <Ionicons name="help-circle-outline" size={28} color={colors.iconSecondary} />
              <Text style={[styles.quizTitle, { color: colors.textPrimary }]}>
                {t('quiz.empty.title')}
              </Text>
              <Text style={[styles.quizDescription, { color: colors.textSecondary,
                                                      textAlign: 'center' }]}>
                {t('quiz.empty.body')}
              </Text>
            </View>
          )}
          {quizzes.map((quiz) => (
            <View key={quiz.id} style={[styles.quizCard, { backgroundColor: colors.cardBackground }]}>
              <View style={styles.quizHeader}>
                <View style={styles.quizTitleRow}>
                  <Text style={[styles.quizTitle, { color: colors.textPrimary }]}>{quiz.title}</Text>
                  <View style={[styles.statusBadge, { backgroundColor: getStatusColor(quiz.status) }]}>
                    <Text style={[styles.statusBadgeText, { color: colors.badgeText }]}>{getStatusLabel(quiz.status)}</Text>
                  </View>
                </View>
                <Text style={[styles.quizDescription, { color: colors.textSecondary }]}>{quiz.description}</Text>
              </View>
              <View style={styles.quizStats}>
                <View style={styles.statItem}>
                  <Ionicons name="time" size={14} color={colors.textTertiary} />
                  <Text style={[styles.statText, { color: colors.textSecondary }]}>{quiz.duration} min</Text>
                </View>
                <View style={styles.statItem}>
                  <Ionicons name="bulb" size={14} color={colors.textTertiary} />
                  <Text style={[styles.statText, { color: colors.textSecondary }]}>{quiz.totalQuestions} questions</Text>
                </View>
                <View style={styles.statItem}>
                  <Text style={[styles.statText, { color: colors.textSecondary }]}>Threshold : {quiz.passingScore}%</Text>
                </View>
                <View style={styles.statItem}>
                  <Text style={[styles.statText, { color: colors.textSecondary }]}>Tentatives : {quiz.attempts}/{quiz.maxAttempts}</Text>
                </View>
              </View>
              {(quiz.status === 'completed' || quiz.status === 'failed') && (
                <View style={styles.completedInfo}>
                  <Ionicons name="checkmark-circle" size={16} color={quiz.status === 'completed' ? colors.success : colors.error} />
                  <Text style={[styles.completedText, { color: colors.textSecondary }]}>Score : {quiz.userScore}%</Text>
                </View>
              )}
              <View style={styles.quizActions}>
                {/* Un quiz SANS question n'a rien à jouer : le démarrer ouvrait un écran vide
                    (currentQuestion undefined). On le dit au lieu d'inviter à cliquer. */}
                {quiz.questions.length === 0 ? (
                  <View style={[styles.primaryButton, { backgroundColor: colors.surfaceSecondary }]}>
                    <Text style={[styles.primaryButtonText, { color: colors.textTertiary }]}>
                      {t('quiz.empty.storyBuilder')}
                    </Text>
                  </View>
                ) : quiz.status === 'not_started' || quiz.status === 'in_progress' ? (
                  <TouchableOpacity style={[styles.primaryButton, glow(ACCENT, true, isDarkTheme)]} onPress={() => handleStartQuiz(quiz.id)}>
                    <Text style={[styles.primaryButtonText, { color: glowText(ACCENT, true, isDarkTheme) }]}>
                      {t(quiz.status === 'in_progress' ? 'quiz.continue' : 'quiz.start')}
                    </Text>
                  </TouchableOpacity>
                ) : (
                  <View style={styles.completedActions}>
                    <TouchableOpacity style={[styles.secondaryButton, { borderColor: colors.border }]} onPress={() => {
                      // On REJOUE le récapitulatif avec la copie conservée : sans elle, la revue
                      // n'affichait que le corrigé (aucune trace de ce que le joueur avait coché).
                      const saved = answersByQuiz[quiz.id];
                      setSelectedQuiz(quiz.id);
                      setCurrentQuestionIndex(0);
                      setUserAnswers(saved?.answers ?? {});
                      setFinalScore(saved?.score ?? quiz.userScore ?? 0);
                      setQuizCompleted(true);
                    }}>
                      <Ionicons name="eye" size={14} color={ACCENT} />
                      <Text style={[styles.secondaryButtonText, { color: ACCENT }]}>{t('quiz.reviewAnswers')}</Text>
                    </TouchableOpacity>
                    {quiz.attempts < quiz.maxAttempts && (
                      <TouchableOpacity style={[styles.secondaryButton, { borderColor: colors.border }]} onPress={() => handleStartQuiz(quiz.id)}>
                        <Ionicons name="refresh" size={14} color={ACCENT} />
                        <Text style={[styles.secondaryButtonText, { color: ACCENT }]}>{t('quiz.restart')}</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                )}
              </View>
            </View>
          ))}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const makeStyles = (C: Palette) => StyleSheet.create({
  container: { flex: 1, minHeight: 0 },
  header: { borderBottomWidth: 1, paddingHorizontal: 20, paddingVertical: 16 },
  headerContent: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  backButton: { padding: 8, marginLeft: -8, marginRight: 12 },
  headerInfo: { flex: 1 },
  headerTitle: { fontSize: 18, fontWeight: '600', marginBottom: 2 },
  headerSubtitle: { fontSize: 12 },
  headerRight: { width: 40 },
  timerContainer: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  timerText: { fontSize: 14, fontWeight: '500' },
  reviewBadgeHeader: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 12 },
  reviewBadgeHeaderText: { fontSize: 12, fontWeight: '500' },
  progressBar: { height: 4, borderRadius: 2, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 2 },
  content: { flex: 1, minHeight: 0 },
  contentPadding: { padding: 16 },
  centerContent: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 },
  centerTitle: { fontSize: 20, fontWeight: '600', marginBottom: 16 },
  scoreCard: { borderRadius: 16, padding: 20, marginBottom: 20, alignItems: 'center' },
  resultIcon: { width: 72, height: 72, borderRadius: 36, alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  resultTitle: { fontSize: 22, fontWeight: '700', marginBottom: 4 },
  resultSubtitle: { fontSize: 14, textAlign: 'center', marginBottom: 20 },
  scoreRow: { flexDirection: 'row', alignItems: 'center', gap: 8, width: '100%' },
  scoreItem: { flex: 1, alignItems: 'center' },
  scoreNumber: { fontSize: 22, fontWeight: '700', marginBottom: 2 },
  scoreLabel: { fontSize: 11 },
  scoreDivider: { width: 1, height: 40 },
  sectionTitle: { fontSize: 17, fontWeight: '700', marginBottom: 12 },
  // Liseré d'accent gauche RECTILIGNE (coins gauches carrés, cf. squareLeftCorners).
  reviewCard: { borderRadius: 12, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, padding: 16, marginBottom: 16, borderLeftWidth: 4 },
  reviewHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  reviewBadge: { width: 20, height: 20, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  reviewQuestionNum: { fontSize: 11, fontWeight: '600' },
  reviewQuestionText: { fontSize: 15, fontWeight: '600', marginBottom: 12, lineHeight: 22 },
  reviewOptions: { gap: 8, marginBottom: 12 },
  reviewOption: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10 },
  reviewOptionText: { fontSize: 13, flex: 1, marginRight: 8 },
  // Liseré gauche RECTILIGNE en accent (coins gauches carrés, cf. squareLeftCorners).
  explanationBox: { borderWidth: 1, borderLeftWidth: 3, borderRadius: 10, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, padding: 12 },
  explanationLbl: { fontSize: 12, fontWeight: '700', marginBottom: 4 },
  explanationText: { fontSize: 13, lineHeight: 19 },
  questionCard: { borderRadius: 12, padding: 16, marginBottom: 16 },
  questionText: { fontSize: 17, fontWeight: '500', marginBottom: 8, lineHeight: 24 },
  choiceHint: { fontSize: 12, fontStyle: 'italic', marginBottom: 14 },
  optionsContainer: { gap: 10 },
  optionItem: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderRadius: 8, borderWidth: 1, padding: 14 },
  optionText: { fontSize: 14, flex: 1, marginRight: 8 },
  explanationCard: { borderRadius: 12, padding: 16 },
  navigationBar: { borderTopWidth: 1, paddingHorizontal: 16, paddingVertical: 12 },
  navigationButtons: { flexDirection: 'row', gap: 12 },
  pillRow: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8 },
  navButton: { flex: 1, paddingVertical: 12, paddingHorizontal: 24, borderRadius: 8, alignItems: 'center', borderWidth: 1 },
  navButtonDisabled: { opacity: 0.5 },
  navButtonText: { fontSize: 15, fontWeight: '600' },
  closePill: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, borderWidth: 1, backgroundColor: 'transparent' },
  closePillText: { fontSize: 14, fontWeight: '600' },
  nextPill: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20 },
  nextPillText: { color: '#FFF', fontSize: 14, fontWeight: '600' },
  quizCard: { borderRadius: 12, padding: 16, marginBottom: 16 },
  quizHeader: { marginBottom: 12 },
  quizTitleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  quizTitle: { fontSize: 16, fontWeight: '600', flex: 1 },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 12 },
  statusBadgeText: { fontSize: 11, fontWeight: '600' },
  quizDescription: { fontSize: 13, lineHeight: 19 },
  quizStats: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 12 },
  statItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  statText: { fontSize: 12 },
  completedInfo: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 },
  completedText: { fontSize: 12 },
  quizActions: { gap: 8 },
  completedActions: { flexDirection: 'row', gap: 8 },
  actionButtons: { gap: 12, marginTop: 8, marginBottom: 24 },
  // Bouton COMPACT aligné sur le dashboard (pas d'aplat pleine largeur) : largeur au contenu,
  // padding réduit. `alignSelf:'flex-start'` neutralise le stretch par défaut de RN.
  primaryButton: { alignSelf: 'flex-start', paddingVertical: 9, paddingHorizontal: 18, borderRadius: 8, borderWidth: 1, alignItems: 'center' },
  primaryButtonText: { fontSize: 13, fontWeight: '700' },
  secondaryButton: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 11, paddingHorizontal: 16, borderRadius: 8, borderWidth: 1, gap: 6 },
  secondaryButtonText: { fontSize: 14, fontWeight: '500' },
});
